import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Compass, ClipboardText, ChartLineUp, Eye } from '@phosphor-icons/react';

const prototypes = [
  {
    id: 'participant-dashboard',
    to: '/prototypes/workforce/participant-dashboard',
    title: 'Participant Welcome Dashboard',
    subtitle: 'The first screen a workforce participant sees after onboarding.',
    icon: Compass,
    persona: 'Participant',
    testId: 'proto-link-participant-dashboard',
  },
  {
    id: 'assessment',
    to: '/prototypes/workforce/assessment',
    title: 'Financial Readiness Assessment',
    subtitle: 'The baseline (and follow-up) assessment participants complete to unlock their action plan.',
    icon: ClipboardText,
    persona: 'Participant',
    testId: 'proto-link-assessment',
  },
  {
    id: 'admin-outcomes',
    to: '/prototypes/workforce/admin-outcomes',
    title: 'Program Administrator Outcomes Dashboard',
    subtitle: 'De-identified aggregate outcomes for grant reporting and program review.',
    icon: ChartLineUp,
    persona: 'Program Administrator',
    testId: 'proto-link-admin-outcomes',
  },
];

const PrototypeIndex = () => {
  return (
    <div className="min-h-screen bg-surface-subtle font-body text-ink">
      <div className="w-full bg-brand-gold text-brand-navy text-center py-2 px-4 text-xs font-semibold tracking-wide uppercase">
        <span className="inline-flex items-center gap-2">
          <Eye size={14} weight="bold" />
          Prototype — For Product Review Only · Not connected to live data
        </span>
      </div>

      <header className="bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <div className="flex items-center gap-3 mb-3">
            <div className="text-[11px] tracking-widest uppercase text-brand-gold font-semibold">
              PLANIVANCE AI · Workforce Navigator™
            </div>
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-brand-navy bg-brand-gold px-2.5 py-1 rounded-full"
              data-testid="planning-status-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-brand-navy" />
              Planning Phase Complete · Ready for Founder Approval
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl leading-tight mb-3" data-testid="prototype-index-title">
            Workforce Navigator™ Prototype Review Center
          </h1>
          <p className="text-white/70 text-base max-w-3xl leading-relaxed">
            This review center presents the three Phase 1 prototype screens for Workforce Navigator™.
            Its purpose is to give you a clear, visual read on how the eight approved product decisions
            come to life — before any full implementation begins. Every screen uses synthetic data from
            the <em>Sample Workforce Partner</em> demo tenant. Nothing here writes to a database or
            affects the live Consumer beta.
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Review checklist</div>
          <p className="text-ink-muted text-sm mt-2 max-w-3xl">
            For each screen, please note: (1) does the information hierarchy feel right for the persona,
            (2) is anything missing that would be critical to a real partner deployment, (3) any tone/language
            adjustments. No code will be written for full functionality until you approve.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {prototypes.map((p, idx) => {
            const Icon = p.icon;
            return (
              <Link
                key={p.id}
                to={p.to}
                data-testid={p.testId}
                className="group bg-white rounded-2xl p-6 shadow-fluent-sm hover:shadow-fluent-md
                           border border-surface-border hover:border-brand-gold
                           transition-all duration-200 flex flex-col"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-11 h-11 rounded-xl bg-brand-navy/5 text-brand-navy flex items-center justify-center group-hover:bg-brand-gold/10 group-hover:text-brand-navy transition">
                    <Icon size={22} weight="duotone" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-ink-soft bg-surface-muted px-2 py-1 rounded-full">
                    {p.persona}
                  </span>
                </div>
                <div className="text-[11px] uppercase tracking-widest text-ink-faint font-semibold mb-1">
                  Screen {idx + 1} of 3
                </div>
                <h2 className="font-heading text-xl text-ink mb-2 leading-snug">{p.title}</h2>
                <p className="text-sm text-ink-muted flex-1">{p.subtitle}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-brand-navy font-semibold text-sm group-hover:text-brand-gold transition-colors">
                  Review this screen <ArrowRight size={16} weight="bold" />
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-14 rounded-2xl bg-white p-8 border border-surface-border shadow-fluent-sm">
          <h3 className="font-heading text-lg text-ink mb-3">Reminder — what these prototypes are</h3>
          <ul className="text-sm text-ink-muted space-y-2 list-disc list-inside">
            <li>Visual + interaction design for founder review — no live data, no persistence, no back-end calls.</li>
            <li>All copy, labels, and metrics are placeholder-safe (no real partner identifying information).</li>
            <li>Approval unlocks Phase 1 MVP build — a ~4-week implementation feature-flagged to Workforce edition only.</li>
            <li>Existing Consumer edition beta is not affected by these prototype routes.</li>
          </ul>
        </div>
      </main>

      <footer className="mt-16 border-t border-surface-border py-6 text-center text-xs text-ink-faint">
        © 2026 PLANIVANCE AI · Workforce Navigator™ Prototype
      </footer>
    </div>
  );
};

export default PrototypeIndex;
